import React, { useState, useEffect, useCallback } from 'react';
import SectionText from './section_text';
import SectionImage from './section_image';
import SectionInstagram from './section_instagram';
import SectionVideo from './section_video';
import SectionNote from './section_note';
import SectionCourse from './section_course';
import reorder_function from './reorder_function';
import axios from '../axios';
import TypeSelect from './type_select';
import EnterSection from './enter_section';
import {
  TEXT,
  HEADING_LARGE,
  HEADING_MEDIUM,
  HEADING_SMALL,
  IMAGE,
  INSTAGRAM,
  YOUTUBE,
  FREE,
  NOTE,
  COURSE,
} from './const';

const Contents = ({
  isApp,
  userAgent,
  foreignKeyId,
  model,
  isCoursesDisplayed,
  displayNextButtonFunc,
  hideNextButtonFunc,
}) => {
  const [contents, setContents] = useState([]);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    axios
      .post('/api/content/load_contents', {
        foreign_key_id: foreignKeyId,
        model,
      })
      .then((response) => {
        setContents(response.data);
        setSelectedIndex(response.data.length);
      });
  }, []);

  const updateContentsApi = useCallback((contents) => {
    const sortedContents = contents
      .slice()
      .sort((a, b) => a['sort_order'] - b['sort_order'])
      .map((data, index) => ({
        ...data,
        sort_order: index + 1,
      }));

    const contentsForApi = sortedContents.reduce((acc, data, index) => {
      acc[index] = data;
      return acc;
    }, {});

    axios
      .post('/api/content/update_contents', {
        contents: contentsForApi,
        foreign_key_id: foreignKeyId,
        model,
      })
      .then((response) => {
        if (response.status !== 200) {
          alert('コンテンツの更新に失敗しました。');
        } else {
          setContents((prevContents) => {
            return prevContents.map((content, index) => ({
              ...content,
              id: response.data[index].id,
            }));
          });
        }
      });
  }, []);

  useEffect(() => {
    const updateFunc = () => {
      const sorted_ids = $('#contents_sort').sortable('toArray');
      setContents((prev) => {
        const sortedContents = prev.map((content, index) => {
          content.sort_order = sorted_ids.indexOf(content.id.toString()) + 1;
          return content;
        });
        updateContentsApi(sortedContents);
        return sortedContents;
      });
    };
    reorder_function(updateFunc);
  }, []);

  useEffect(() => {
    if (window.instgrm) {
      window.instgrm.Embeds.process();
    }
  }, [contents]);

  const updateContent = useCallback((index, content) => {
    setContents((prev) => {
      const newContents = [...prev];
      newContents[index] = content;
      updateContentsApi(newContents);
      return newContents;
    });
  }, []);

  const deleteContent = useCallback((index) => {
    setContents((prev) => {
      const newContents = [...prev];
      newContents.splice(index, 1);
      newContents.forEach((data, index) => {
        data.sort_order = index + 1;
      });
      updateContentsApi(newContents);
      return newContents;
    });
  }, []);

  const addContent = useCallback((content) => {
    setContents((prev) => {
      const newContents = [...prev];
      newContents.push(content);
      newContents.forEach((data, index) => {
        data.sort_order = index + 1;
      });
      updateContentsApi(newContents);
      return newContents;
    });
  }, []);

  // ブログ作成のスマホだけ: 行のメニューの「並べかえ」から、並べかえモード（blog2023/edit.js）に切り替える
  const onReorder =
    model === 'Blog' && userAgent !== 'pc'
      ? () => window.dispatchEvent(new Event('blog-editor:reorder'))
      : undefined;

  const renderContent = useCallback(
    (content, index) => {
      switch (content.content_type) {
        case TEXT:
        case HEADING_LARGE:
        case HEADING_MEDIUM:
        case HEADING_SMALL:
          return (
            <SectionText
              index={index}
              content={content}
              userAgent={userAgent}
              updateContent={(content) => updateContent(index, content)}
              isSelected={selectedIndex === index}
              setSelectedIndex={setSelectedIndex}
              deleteContent={() => deleteContent(index)}
            />
          );
        case IMAGE:
          return (
            <SectionImage
              index={index}
              content={content}
              userAgent={userAgent}
              onReorder={onReorder}
              isBlogEditor={model === 'Blog'}
              updateContent={(content) => updateContent(index, content)}
              deleteContent={() => deleteContent(index)}
              setContents={setContents}
              isSelected={selectedIndex === index}
              setSelectedIndex={setSelectedIndex}
              foreignKeyId={foreignKeyId}
              model={model}
            />
          );
        case INSTAGRAM:
          return (
            <SectionInstagram
              index={index}
              content={content}
              userAgent={userAgent}
              onReorder={onReorder}
              isBlogEditor={model === 'Blog'}
              updateContent={(content) => updateContent(index, content)}
              isSelected={selectedIndex === index}
              setSelectedIndex={setSelectedIndex}
              deleteContent={() => deleteContent(index)}
              hideNextButtonFunc={hideNextButtonFunc}
              displayNextButtonFunc={displayNextButtonFunc}
            />
          );
        case YOUTUBE:
          return (
            <SectionVideo
              index={index}
              content={content}
              userAgent={userAgent}
              onReorder={onReorder}
              isBlogEditor={model === 'Blog'}
              updateContent={(content) => updateContent(index, content)}
              isSelected={selectedIndex === index}
              setSelectedIndex={setSelectedIndex}
              deleteContent={() => deleteContent(index)}
              hideNextButtonFunc={hideNextButtonFunc}
              displayNextButtonFunc={displayNextButtonFunc}
            />
          );
        case NOTE:
          return (
            <SectionNote
              index={index}
              content={content}
              userAgent={userAgent}
              updateContent={(content) => updateContent(index, content)}
              isSelected={selectedIndex === index}
              setSelectedIndex={setSelectedIndex}
              deleteContent={() => deleteContent(index)}
              hideNextButtonFunc={hideNextButtonFunc}
              displayNextButtonFunc={displayNextButtonFunc}
            />
          );
        case FREE:
          return '無料';
        case COURSE:
          return <SectionCourse content={content} />;
        default:
          return null;
      }
    },
    [contents, selectedIndex]
  );

  return (
    <div>
      <main className="bg-white pt-3">
        <div id="contents_sort" className="single-body single-body-margin">
          {contents
            .sort((a, b) => a['sort_order'] - b['sort_order'])
            .map((content, index) => {
              return (
                <div
                  className={
                    (hoveredIndex === index || selectedIndex === index) &&
                    userAgent === 'pc'
                      ? 'bg-light'
                      : 'bg-white'
                  }
                  key={`contentItem_${content.id}_${index}`}
                  id={content.id}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <div className="container col-md-8 pl-2 pr-2">
                    <div className="d-flex">
                      {(hoveredIndex === index || selectedIndex === index) &&
                      content.content_type !== IMAGE &&
                      content.content_type !== INSTAGRAM &&
                      content.content_type !== YOUTUBE &&
                      userAgent === 'pc' ? (
                        <div style={{ marginLeft: '-40px', paddingRight: 8 }}>
                          <TypeSelect
                            isCoursesDisplayed={false}
                            onChangeType={(type) => {
                              const newContent = {
                                ...content,
                                content_type: type,
                              };
                              updateContent(index, newContent);
                            }}
                            content={content}
                            isEnterSection={false}
                            deleteContent={() => {
                              deleteContent(index);
                            }}
                            userAgent={userAgent}
                            model={model}
                          />
                        </div>
                      ) : null}
                      {renderContent(content, index)}
                      <div className="d-flex flex-column align-items-center justify-content-center col-2">
                        <span
                          className="sortable-btn btn btn-xs btn-default border px-2"
                          style={{
                            color: '#999',
                            zIndex: 19,
                            cursor: 'grabbing',
                          }}
                        >
                          <i className="fas fa-arrows-alt-v" />
                        </span>
                      </div>
                      <div className="d-flex flex-column align-items-center justify-content-center col-2">
                        {userAgent !== 'pc' ? (
                          <TypeSelect
                            isCoursesDisplayed={false}
                            onChangeType={(type) => {
                              const newContent = {
                                ...content,
                                content_type: type,
                              };
                              updateContent(index, newContent);
                            }}
                            content={content}
                            isEnterSection={false}
                            deleteContent={() => {
                              deleteContent(index);
                            }}
                            userAgent={userAgent}
                            model={model}
                          />
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          <EnterSection
            index={contents.length}
            userAgent={userAgent}
            addContent={(content) => addContent(content)}
            isSelected={selectedIndex === contents.length}
            setSelectedIndex={setSelectedIndex}
            setContents={setContents}
            foreignKeyId={foreignKeyId}
            model={model}
            hideNextButtonFunc={hideNextButtonFunc}
            displayNextButtonFunc={displayNextButtonFunc}
          />
        </div>
      </main>
    </div>
  );
};

export default Contents;
